/**
 * Formatting utilities for display values
 */

import { formatUnits, getAddress } from "viem";

/**
 * Format a wallet address for display: checksummed and truncated.
 * Uses viem's getAddress() for EIP-55 checksumming.
 * Falls back to raw truncation if checksumming fails.
 *
 * @param address - Hex wallet address (with 0x prefix)
 * @returns Truncated checksummed address like "0xd8dA...6045"
 */
export function formatAddress(address: string): string {
	try {
		const checksummed = getAddress(address);
		return `${checksummed.slice(0, 6)}...${checksummed.slice(-4)}`;
	} catch {
		return `${address.slice(0, 6)}...${address.slice(-4)}`;
	}
}

/** Format native balance for display (max 4 decimals). */
export function formatNativeBalance(balance: bigint | undefined): string {
	if (balance === undefined) return "0";
	const raw = formatUnits(balance, 18);
	const num = Number.parseFloat(raw);
	if (num === 0) return "0";
	if (num < 0.0001) return "<0.0001";
	return num.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

/** Format a token amount (bigint) for display, truncated to maxDecimals. */
export function formatTokenAmount(amount: bigint, decimals: number, maxDecimals = 6): string {
	const raw = formatUnits(amount, decimals);
	const num = Number.parseFloat(raw);
	if (num === 0) return "0";
	if (num < 10 ** -maxDecimals) return `<${10 ** -maxDecimals}`;
	// For large numbers, reduce decimal places to keep it readable
	const intDigits = Math.floor(Math.abs(num)).toString().length;
	const displayDecimals = Math.max(0, Math.min(maxDecimals, 10 - intDigits));
	return num.toFixed(displayDecimals).replace(/0+$/, "").replace(/\.$/, "");
}
